import React from 'react';
import { useAuth, PermissionGuard, RoleGuard } from '@/features/auth';
import PageMeta from '@/shared/components/PageMeta';
import { ButtonLink, Card, CardContent, CardMedia, CardTitle } from '@/shared/components';

const TILE_BODY_CLASSES = 'flex flex-auto flex-col';

export const Home: React.FC = () => {
    const { user } = useAuth();

    if (!user) {
        return (
            <>
                <PageMeta title="Home" description="Your MotorPlan dashboard" />
                <div className="flex items-center justify-center p-8">
                    <div className="text-center">
                        <h2 className="text-xl font-semibold text-mp-foreground">
                            Loading user data...
                        </h2>
                    </div>
                </div>
            </>
        );
    }

    // Extract first name from user name
    const forename = user.name.split(' ')[0];

    return (
        <>
            <PageMeta
                title="Home"
                description="Welcome to your MotorPlan dashboard - manage your business operations"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
                <div className="col-span-1 md:col-span-2 lg:col-span-3 space-y-6 mb-6">
                    <h1 className="text-2xl font-semibold text-mp-foreground">
                        Welcome to MotorPlan, {forename}!
                    </h1>
                    <p className="text-mp-foreground">
                        This is the home page of your dashboard. Here you can launch into the
                        various sections of the system, such as managing products, orders,
                        customers, and more.
                    </p>
                </div>

                <PermissionGuard permission={{ resource: 'quote', action: 'browse' }}>
                    <Card className="flex w-full max-w-96 flex-col">
                        <CardMedia className="h-48 w-full">
                            <img
                                src="https://images.pexels.com/photos/70912/pexels-photo-70912.jpeg?cs=srgb&dl=pexels-tdcat-70912.jpg&fm=jpg"
                                alt="Service Plan"
                                className="w-full h-full object-cover"
                            />
                        </CardMedia>
                        <CardContent className={TILE_BODY_CLASSES}>
                            <CardTitle>
                                New Service Plan
                            </CardTitle>
                            <p className="mb-4 grow text-sm text-mp-muted">
                                Start here to create a new Service Plan for your customer.
                            </p>
                            <div className="flex flex-wrap items-start justify-end gap-2">
                                <ButtonLink
                                    to="/service-plans/new"
                                    size="sm"
                                >
                                    Create New
                                </ButtonLink>
                            </div>
                        </CardContent>
                    </Card>
                </PermissionGuard>

                <RoleGuard requiredRoles={['admin']} showFallback={false}>
                    <Card className="flex w-full max-w-96 flex-col">
                        <CardMedia className="h-48 w-full">
                            <img
                                src="https://images.pexels.com/photos/162553/keys-workshop-mechanic-tools-162553.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                                alt="Maintenance Plan"
                                className="w-full h-full object-cover object-[0%_28%]"
                            />
                        </CardMedia>
                        <CardContent className={TILE_BODY_CLASSES}>
                            <CardTitle>
                                Register Maintenance Plan
                            </CardTitle>
                            <p className="mb-4 grow text-sm text-mp-muted">
                                Register a new Maintenance Plan.
                            </p>
                            <div className="flex flex-wrap items-start justify-end gap-2">
                                <ButtonLink
                                    to="/maintenance-plans/new"
                                    size="sm"
                                >
                                    Register Plan
                                </ButtonLink>
                            </div>
                        </CardContent>
                    </Card>
                </RoleGuard>

                <Card className="flex w-full max-w-96 flex-col">
                    <CardMedia className="h-48 w-full">
                        <img
                            src="https://images.pexels.com/photos/3184298/pexels-photo-3184298.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
                            alt="User Profile"
                            className="w-full h-full object-cover"
                        />
                    </CardMedia>
                    <CardContent className={TILE_BODY_CLASSES}>
                        <CardTitle>
                            My Profile
                        </CardTitle>
                        <p className="mb-4 grow text-sm text-mp-muted">
                            Manage your account settings and preferences.
                        </p>
                        <div className="flex flex-wrap items-start justify-end gap-2">
                            <ButtonLink
                                to="/profile"
                                size="sm"
                            >
                                View Profile
                            </ButtonLink>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </>
    );
};

// This is the main dashboard page for the MotorPlan application.
